import { useEffect } from "react";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeMeta(selector) {
  const el = document.head.querySelector(selector);
  if (el) el.remove();
}

// Per-route <title>, meta description, canonical, and (optionally) a prerender-status-code
// + noindex for gone/unavailable pages. Picked up by JS-rendering crawlers and the platform
// prerenderer. No-op for any field left undefined; stale tags are cleared on navigation.
export function useSeo({ title, description, canonical, image, type, statusCode, noindex } = {}) {
  useEffect(() => {
    if (title) {
      document.title = title;
      upsertMeta("property", "og:title", title);
      upsertMeta("name", "twitter:title", title);
    }
    if (description) {
      upsertMeta("name", "description", description);
      upsertMeta("property", "og:description", description);
      upsertMeta("name", "twitter:description", description);
    }
    if (canonical) {
      upsertCanonical(canonical);
      upsertMeta("property", "og:url", canonical);
    }
    upsertMeta("property", "og:type", type || "website");
    // Rich social preview image. Only present for live pages — gone/unavailable pages
    // pass no image so they never render a share card (kept in step with noindex/410).
    if (image) {
      upsertMeta("property", "og:image", image);
      upsertMeta("name", "twitter:image", image);
      upsertMeta("name", "twitter:card", "summary_large_image");
    } else {
      removeMeta('meta[property="og:image"]');
      removeMeta('meta[name="twitter:image"]');
      upsertMeta("name", "twitter:card", "summary");
    }
    // Soft-404 / 410: tell the prerenderer the real HTTP status for missing/sold listings.
    if (statusCode) {
      upsertMeta("name", "prerender-status-code", String(statusCode));
    } else {
      removeMeta('meta[name="prerender-status-code"]');
    }
    // Keep unavailable pages out of the index.
    if (noindex) {
      upsertMeta("name", "robots", "noindex, follow");
    } else {
      removeMeta('meta[name="robots"]');
    }
    return () => {
      removeMeta('meta[name="prerender-status-code"]');
      removeMeta('meta[name="robots"]');
      removeMeta('meta[property="og:image"]');
      removeMeta('meta[name="twitter:image"]');
    };
  }, [title, description, canonical, image, type, statusCode, noindex]);
}
